import { useEffect, useRef, useState } from "react";

// left: 37, up: 38, right: 39, down: 40,
// spacebar: 32, pageup: 33, pagedown: 34, end: 35, home: 36
const keys: { [key: number]: number } = { 37: 1, 38: 1, 39: 1, 40: 1 };

function preventDefault(e: Event) {
  e.preventDefault();
}

function preventDefaultForScrollKeys(e: KeyboardEvent) {
  if (keys[e.keyCode]) {
    preventDefault(e);
    return false;
  }
}

export function useDisableScroll() {
  useEffect(() => {
    // Disable scroll
    window.addEventListener("DOMMouseScroll", preventDefault, {
      passive: false,
    });
    window.addEventListener("wheel", preventDefault, { passive: false });
    window.addEventListener("touchmove", preventDefault, { passive: false });
    window.addEventListener("keydown", preventDefaultForScrollKeys, false);

    // Enable scroll when component unmounts
    return () => {
      window.removeEventListener("DOMMouseScroll", preventDefault, false);
      window.removeEventListener("wheel", preventDefault, false);
      window.removeEventListener("touchmove", preventDefault, false);
      window.removeEventListener("keydown", preventDefaultForScrollKeys, false);
    };
  }, []); // Empty array ensures effect is only run on mount and unmount
}

export function useDisableScrollOnElement(elementId: string) {
  const elementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const element = document.getElementById(elementId);
    elementRef.current = element;

    if (element) {
      // Disable scroll on this element
      element.addEventListener("DOMMouseScroll", preventDefault, {
        passive: false,
      });
      element.addEventListener("wheel", preventDefault, { passive: false });
      element.addEventListener("touchmove", preventDefault, { passive: false });
      element.addEventListener("keydown", preventDefaultForScrollKeys, false);
    }

    // Enable scroll when component unmounts
    return () => {
      if (elementRef.current) {
        elementRef.current.removeEventListener(
          "DOMMouseScroll",
          preventDefault,
          false
        );
        elementRef.current.removeEventListener("wheel", preventDefault, false);
        elementRef.current.removeEventListener(
          "touchmove",
          preventDefault,
          false
        );
        elementRef.current.removeEventListener(
          "keydown",
          preventDefaultForScrollKeys,
          false
        );
      }
    };
  }, []); // Empty array ensures effect is only run on mount and unmount
}

export function useToggleScrollOnElement(elementId: string) {
  const elementRef = useRef<HTMLElement | null>(null);
  const [isScrollDisabled, setScrollDisabled] = useState(false);

  const toggleScroll = () => {
    const element = document.getElementById(elementId);
    elementRef.current = element;

    if (!element) {
      return;
    }

    if (isScrollDisabled) {
      // Enable scroll on this element
      element.removeEventListener("DOMMouseScroll", preventDefault, false);
      element.removeEventListener("wheel", preventDefault, false);
      element.removeEventListener("touchmove", preventDefault, false);
      element.removeEventListener(
        "keydown",
        preventDefaultForScrollKeys,
        false
      );
    } else {
      // Disable scroll on this element
      element.addEventListener("DOMMouseScroll", preventDefault, {
        passive: false,
      });
      element.addEventListener("wheel", preventDefault, { passive: false });
      element.addEventListener("touchmove", preventDefault, { passive: false });
      element.addEventListener("keydown", preventDefaultForScrollKeys, false);
    }

    setScrollDisabled(!isScrollDisabled);
  };

  useEffect(() => {
    return () => {
      if (elementRef.current) {
        elementRef.current.removeEventListener(
          "DOMMouseScroll",
          preventDefault,
          false
        );
        elementRef.current.removeEventListener("wheel", preventDefault, false);
        elementRef.current.removeEventListener(
          "touchmove",
          preventDefault,
          false
        );
        elementRef.current.removeEventListener(
          "keydown",
          preventDefaultForScrollKeys,
          false
        );
      }
    };
  }, []); // Empty array ensures effect is only run on mount and unmount

  return toggleScroll;
}

export function useToggleScrollOnLowZIndexElements() {
  const [elements, setElements] = useState<HTMLElement[]>([]);
  const [isScrollDisabled, setScrollDisabled] = useState(false);

  const toggleScroll = () => {
    setScrollDisabled(!isScrollDisabled);
  };

  useEffect(() => {
    const allElements = Array.from(document.body.getElementsByTagName("*"));

    const lowZIndexElements = allElements.filter((element) => {
      const zIndex = window.getComputedStyle(element).zIndex;
      return (
        zIndex === "auto" || (!isNaN(parseInt(zIndex)) && parseInt(zIndex) < 50)
      );
    }) as HTMLElement[];

    setElements(lowZIndexElements);
  }, []);

  useEffect(() => {
    elements.forEach((element) => {
      if (isScrollDisabled) {
        element.addEventListener("DOMMouseScroll", preventDefault, {
          passive: false,
        } as AddEventListenerOptions);
        element.addEventListener("wheel", preventDefault, {
          passive: false,
        } as AddEventListenerOptions);
        element.addEventListener("touchmove", preventDefault, {
          passive: false,
        } as AddEventListenerOptions);
        element.addEventListener("keydown", preventDefaultForScrollKeys, false);
      } else {
        element.removeEventListener("DOMMouseScroll", preventDefault, false);
        element.removeEventListener("wheel", preventDefault, false);
        element.removeEventListener("touchmove", preventDefault, false);
        element.removeEventListener(
          "keydown",
          preventDefaultForScrollKeys,
          false
        );
      }
    });
  }, [elements, isScrollDisabled]);

  return toggleScroll;
}

export type UseDisableBackgroundScrollProps = {
  isDisabled?: boolean;
  elementIdList?: string[];
};

export const useDisableBackgroundScroll = ({
  elementIdList,
  isDisabled = true,
}: UseDisableBackgroundScrollProps): [
  (Element | HTMLElement | null)[],
  (isDisabled: boolean) => void
] => {
  const targetElement = document.getElementById("authenticated_outlet");

  const changeOverflow = (isDisabled: boolean) => {
    const targetList = [
      document.body,
      targetElement,
      ...(elementIdList?.map((id) => document.getElementById(id)) ?? []),
    ];

    targetList.forEach((element) => {
      if (element) {
        element.style.overflow = isDisabled ? "hidden" : "visible";
      }
    });
  };

  useEffect(() => {
    changeOverflow(isDisabled);
  }, [isDisabled]);

  return [
    [
      document.body,
      targetElement,
      ...(elementIdList?.map((id) => document.getElementById(id)) ?? []),
    ],
    changeOverflow,
  ];
};
